import { useRudderstack } from 'analytics/RudderStack';
import { BasketQuery, useBasketUpdateMutation } from 'lib/api';
import { useRouter } from 'next/router';
import provinces from 'provinces';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import { tw } from 'twind';
import { Button } from './Button';
import { Modal } from './Modal';
import PhoneNumberInput from './PhoneNumberInput';
import { pickerStyles, selectStyles } from './PhoneNumberInput/styles';
import { countriesData } from './PhoneNumberInput/utils';
import { useCartStore } from 'store/cart';

export const phoneInputStyle = {
  ...pickerStyles,
  buttonClass: 'h-9 flex items-center w-max mr-3 p-1.5 bg-white border border-[#4F5458]',
  inputClass: 'flex-1 min-w-0 px-3 h-9 border border-[#4F5458]',
};

export const countryDropdownStyle = {
  ...selectStyles,
  control: (a: any) => ({
    ...selectStyles.control(a),
    width: 300,
    border: '1px solid #4F5458',
    borderBottom: undefined,
    borderRadius: 0,
    height: 36,
  }),
  container: (a: any) => ({
    ...selectStyles.container(a),
    marginTop: 0,
    marginBottom: 8,
  }),
  menu: (a: any) => ({
    ...selectStyles.menu(a),
    border: '1px solid #4F5458',
  }),
};

interface InputProps {
  theme:string|null;
  label: string;
  type?: string;
  name: string;
  value: string;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Input: React.FC<InputProps> = ({theme, type = 'text', label, name, value, onChange, required = false, children }) => (
  <label type={type} className={tw`font-medium text-sm  mb-4 flex flex-col w-[300px]`}>
    {label}
    <input required={required} name={name} value={value} onChange={onChange} className={tw`h-9 border-${theme}-primary border py-1 px-2`} />
  </label>
);

export const ModalShipping: React.FC<{
  isOpen: boolean;
  requestClose: () => void;
  basket: BasketQuery['Basket'];
  currentUser: BasketQuery['CartUserCurrent'];
  refresh: Function;
}> = ({ isOpen, requestClose, basket, currentUser, refresh }) => {
  const router = useRouter();
  const [formData, setFormData] = useState({
    name: basket?.shipping_address?.name ?? '',
    email: basket?.email ?? '',
    phone: basket?.shipping_address?.phone ?? currentUser?.phone_number ?? '',
    line1: basket?.shipping_address?.address?.line1 ?? '',
    line2: basket?.shipping_address?.address?.line2 ?? '',
    city: basket?.shipping_address?.address?.city ?? '',
    state: basket?.shipping_address?.address?.state ?? '',
    postal_code: basket?.shipping_address?.address?.postal_code ?? '',
    country: basket?.shipping_address?.address?.country ?? 'US',
  });
  const rudder = useRudderstack();

  const { theme } = useCartStore();


  const [, basketUpdate] = useBasketUpdateMutation();
  const [loading, setIsLoading] = useState(false);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((form) => ({ ...form, [e.target.name]: e.target.value }));
  }, []);

  const handlePhoneNumberChange = useCallback((phone: string) => {
    setFormData((form) => ({ ...form, phone }));
  }, []);
  const handleCountryChange = useCallback((country: any) => {
    setFormData((form) => ({ ...form, country: country.value }));
  }, []);
  const handleStateChange = useCallback((province: any) => {
    setFormData((form) => ({ ...form, state: province.short, country: province.country }));
  }, []);

  useLayoutEffect(
    function () {
      if (isOpen) {
        rudder.track('shipping-modal-open', {
          cart_id: basket.cart_id,
          step: 6,
        });
      }
    },
    [basket.cart_id, rudder, isOpen]
  );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    if (!phoneIsValid) {
      toast.error('Please enter a valid phone number.');
      return;
    }

    if (!formData.state) {
      toast.error('Please enter a state/province');
      return;
    }

    setIsLoading(true);

    const { name, phone, email, ...address } = formData;

    try {
      const response = await basketUpdate({
        input: {
          id: basket.id,
          email,
          shipping_address: {
            name,
            phone,
            address,
          },
        },
      });
      if (response.data) {
        toast.success('Shipping address saved.');

        rudder.track('shipping-modal-saved', {
          cart_id: basket.cart_id,
          step: 7,
        });

        refresh();
        setIsLoading(false);
        requestClose();
      } else {
        // @ts-ignore
        // @TODO: backend returns .errors but TS thinks it should be .error
        throw response.error || response.errors;
      }
    } catch (e) {
      toast.error('An error occured. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Modal open={isOpen} requestClose={requestClose}>
      <form onSubmit={handleSubmit}>
        <div>
          <h3 className={tw`text-lg leading-6 font-medium text-gray-900`}>Shipping Info</h3>
          <div className={tw`my-6`}>
            <Input theme={theme} required type="email" label="Email address*" name="email" value={formData['email']} onChange={handleChange} />
            <Input theme={theme} required label="Name*" name="name" value={formData['name']} onChange={handleChange} />
            <label className={tw`font-medium text-sm  mb-4 flex flex-col w-[300px]`}>
              Phone Number*
              <PhoneNumberInput
                required
                showFlag
                autoFocus={false}
                value={formData.phone}
                onChange={handlePhoneNumberChange}
                isValid={setPhoneIsValid}
                styleConfig={phoneInputStyle}
                selectStyleConfig={countryDropdownStyle}
              />
            </label>

            <Input theme={theme} required label="Address*" name="line1" value={formData['line1']} onChange={handleChange} />
            <Input theme={theme} label="Apartment, suite, etc." name="line2" value={formData['line2']} onChange={handleChange} />
            <Input theme={theme} required label="City*" name="city" value={formData['city']} onChange={handleChange} />
            {router.query.slug === 'neostrata' ? (
              <label className={tw`font-medium text-sm  mb-4 flex flex-col w-[300px]`}>
                State/Province*
                <Select
                  required
                  isSearchable
                  placeholder=""
                  isClearable={false}
                  isMulti={false}
                  value={provinces.find((d) => d.short === formData.state)}
                  onChange={handleStateChange}
                  getOptionValue={(option) => option.short!}
                  getOptionLabel={(option) => option.name!}
                  options={provinces}
                  styles={countryDropdownStyle}
                />
              </label>
            ) : (
              <Input theme={theme} required label="State/Province*" name="state" value={formData['state']} onChange={handleChange} />
            )}

            <Input theme={theme} required label="Zip/Postal Code*" name="postal_code" value={formData['postal_code']} onChange={handleChange} />
            <label className={tw`font-medium text-sm  mb-4 flex flex-col w-[300px]`}>
              Country
              <Select
                isSearchable
                isClearable={false}
                isMulti={false}
                value={countriesData.find((d) => d.value === formData.country)}
                onChange={handleCountryChange}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.name!}
                options={countriesData}
                styles={countryDropdownStyle}
              />
            </label>
          </div>
        </div>
        <div className={tw`mt-5 sm:mt-6`}>
          <Button
            className={tw`inline-flex items-center justify-center px-6 py-3 text-sm leading-4 font-bold bg-${theme} text-white relative disabled:cursor-not-allowed focus:outline-none focus:ring-offset-2 focus:ring-white-500 disabled:opacity-60 text-center`}
            type="submit"
            loading={loading}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};
